import { Component, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {FuseNavigationService,FuseVerticalNavigationComponent,} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MatSidenavModule } from '@angular/material/sidenav';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { CartService } from 'app/core/cart/cart.service';
import { Cart } from 'app/core/cart/cart.types';
import { SidebarComponent } from 'app/layout/common/sidebar/sidebar.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { NavSidebarComponent } from 'app/layout/common/nav-sidebar/nav-sidebar.component';
import { NavSidebarService } from 'app/core/sidebar/nav-sidebar.service';

@Component({
	selector: 'classy-layout',
	templateUrl: './classy.component.html',
	styleUrl: './classy.component.scss',
	encapsulation: ViewEncapsulation.None,
	standalone: true,
	imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    UserComponent,
    MatIconModule,
    MatButtonModule,
    RouterOutlet,
    MatSidenavModule,
    SidebarComponent,
    SearchComponent,
	NavSidebarComponent,
	CommonModule
],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
	isScreenSmall: boolean;
	navigation: Navigation;
	user: User;
	cart: Cart;
	unreadCount: number=2;
	showNavSidebar = false;
	type: string = '';
	disableCartButton = false;

	private _unsubscribeAll: Subject<any> = new Subject<any>();

	/**
	 * Constructor
	 */
	constructor(
		private _navigationService: NavigationService,
		private _userService: UserService,
		private _cartService: CartService,
		private _fuseMediaWatcherService: FuseMediaWatcherService,
		private _fuseNavigationService: FuseNavigationService,
		private readonly _router: Router,
		private _activatedRoute: ActivatedRoute,
		private _changeDetectorRef: ChangeDetectorRef,
		private _navSidebarService: NavSidebarService
	) {
		this._userService.user$
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe((user: User) => {
				if (!user?.name || !user?.email || !user?.image) {
					user = this._userService.authenticatedUser;
				}
				this.user = user;
			});
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Accessors
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Getter for current year
	 */
	get currentYear(): number {
		return new Date().getFullYear();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Lifecycle hooks
	// -----------------------------------------------------------------------------------------------------

	/**
	 * On init
	 */

	ngOnInit(): void {

		this._router.events
			.pipe(
				filter(event => event instanceof NavigationEnd),
				takeUntil(this._unsubscribeAll)
			)
			.subscribe((event: NavigationEnd) => {
				this.disableCartButton = event.urlAfterRedirects.includes('/check-out');
				this._changeDetectorRef.markForCheck();
			});

		const currentUrl = this._router.url;
		this.disableCartButton = currentUrl.includes('/check-out');
		this._changeDetectorRef.markForCheck();

		this._navSidebarService.state$
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe(({ open, type }) => {
				this.showNavSidebar = open;
				this.type = type;
			});
		this._navigationService.navigation$
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe((navigation: Navigation) => {
				this.navigation = navigation;
			});

		// Subscribe to the cart service
		this._cartService.cart$
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe((cart: Cart) => {
				if (!cart?.cartQuantity) {
					this.cart = this._cartService.cartQuantity;
				}
				this.cart = cart;
			});

		// Subscribe to media changes
		this._fuseMediaWatcherService.onMediaChange$
			.pipe(takeUntil(this._unsubscribeAll))
			.subscribe(({ matchingAliases }) => {
				// Check if the screen is small
				this.isScreenSmall = !matchingAliases.includes('md');
			});
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		// Unsubscribe from all subscriptions
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
	}

	// -----------------------------------------------------------------------------------------------------
	// @ Public methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Toggle navigation
	 *
	 * @param name
	 */
	toggleNavigation(name: string): void {
		// Get the navigation
		const navigation =
			this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
				name
			);
		if (navigation) {
			// Toggle the opened status
			navigation.toggle();
		}
	}

	onBackdropClicked(): void {
		// Go back to the list
		this._router.navigate(['./'], { relativeTo: this._activatedRoute });

		// Mark for check
		this._changeDetectorRef.markForCheck();
	}
}
